/**
 * Vector-borne (host–vector) compartmental model — Ross–Macdonald style.
 *
 * Couples an SEIR host (humans/livestock) to an SEI vector (mosquitoes):
 *
 *   HOST:    dSh = -a·b·(Iv/Nh)·Sh
 *            dEh =  a·b·(Iv/Nh)·Sh - σh·Eh
 *            dIh =  σh·Eh - γh·Ih
 *            dRh =  γh·(1-ifr)·Ih
 *            dDh =  γh·ifr·Ih
 *   VECTOR:  dSv =  μv·Nv - a·c·(Ih/Nh)·Sv - μv·Sv
 *            dEv =  a·c·(Ih/Nh)·Sv - (σv+μv)·Ev      (σv = 1/EIP)
 *            dIv =  σv·Ev - μv·Iv
 *
 * Vector births = deaths (μv·Nv into Sv) keep Nv constant; the vector has no
 * recovered class (mosquitoes stay infectious for life). The extrinsic
 * incubation period (EIP) and vector mortality produce the characteristic
 * vector-borne delay that a plain SEIRD cannot.
 *
 * The reproduction number of this 2-host cycle is
 *   R0² = (m · a² · b · c · pSurvive) / (μv · γh),   pSurvive = σv/(σv+μv)
 * where m = Nv/Nh. Our catalog carries the HUMAN R0 (which already embeds the
 * vector cycle) but no vector parameters, so we FIX the vector biology at
 * literature-typical (Aedes) defaults and SOLVE for the effective vector:host
 * ratio m that reproduces the catalog R0 — see calibrateVectorBorne. Override
 * the vector defaults for non-Aedes systems (Culex/RVF, ticks/CCHF, etc.).
 *
 * Host compartments are exposed under the standard S/E/I/R/D names (so existing
 * EpiResult consumers — ensemble, backtest, surfaces — work unchanged); the
 * vector compartments are Sv/Ev/Iv.
 */

import { integrateRk4 } from './rk4.js';
import { ENGINE_VERSION } from './sir.js';
import type { EpiResult, BetaScaler } from './types.js';

export interface SimulateVectorBorneOptions {
  /** Time-varying multiplier on the force of infection on hosts (mirrors the
   *  SEIRD betaScaler) — lets NPIs (vector control, bednets, contact
   *  reduction) modulate vector-borne transmission. */
  betaScaler?: BetaScaler;
}

export interface VectorBorneParams {
  /** Host population (humans or amplifying livestock host). */
  N: number;
  /** Initial infectious hosts. */
  I0: number;
  /** Initial exposed hosts. Default 0. */
  E0?: number;
  /** Initial immune hosts (e.g. prior-immunity / vaccinated). Default 0. */
  R0Pop?: number;
  /** Target basic reproduction number — the catalog HUMAN R0. The vector:host
   *  ratio m is calibrated so the model reproduces this. */
  R0: number;
  /** Host latent→infectious rate σh = 1 / intrinsic incubation period (days). */
  sigmaH: number;
  /** Host recovery rate γh = 1 / host infectious period (days). */
  gammaH: number;
  /** Host infection-fatality ratio, [0,1]. */
  ifr: number;
  durationDays: number;
  /** Time step (days). Default 0.25 (vector FOI can be fast). */
  dtDays?: number;
  // --- vector biology (literature-typical Aedes aegypti defaults; override per vector) ---
  /** Biting rate a (bites per vector per day). Default 0.5. */
  bitingRatePerDay?: number;
  /** Transmission probability per infectious bite, vector→host (b). Default 0.5. */
  vectorCompetenceToHost?: number;
  /** Transmission probability per bite on an infectious host, host→vector (c). Default 0.5. */
  vectorCompetenceToVector?: number;
  /** Extrinsic incubation period (days) — vector E→I. Default 10. */
  extrinsicIncubationDays?: number;
  /** Mean vector lifespan (days); μv = 1/lifespan. Default 14. */
  vectorLifespanDays?: number;
  /** Initial infectious vectors. Default 0 (epidemic seeded by host I0). */
  Iv0?: number;
}

export interface VectorBorneCalibration {
  /** Derived effective vector:host ratio m = Nv/Nh that reproduces the target R0. */
  vectorToHostRatio: number;
  a: number; b: number; c: number;
  extrinsicIncubationDays: number;
  vectorLifespanDays: number;
  /** Vector mortality rate μv = 1/lifespan. */
  muV: number;
  /** Vector incubation rate σv = 1/EIP. */
  sigmaV: number;
  /** Probability a newly-infected vector survives the EIP, σv/(σv+μv). */
  survivalThroughEip: number;
}

/** True if any transmission mode is vector-borne (mosquito/tick/flea/fly) —
 *  the signal that a pathogen should be simulated with the host-vector model
 *  rather than a plain SEIRD. Catalog-agnostic: takes the bare mode strings. */
export function isVectorBorneModes(modes: readonly string[]): boolean {
  return modes.some(m => m.startsWith('vector-'));
}

const def = {
  a: 0.5, b: 0.5, c: 0.5, eip: 10, lifespan: 14,
};

/** Solve for the vector:host ratio m that makes the Ross–Macdonald R0 equal the
 *  target (catalog) R0, holding the vector biology fixed. Pure. */
export function calibrateVectorBorne(p: VectorBorneParams): VectorBorneCalibration {
  const a = p.bitingRatePerDay ?? def.a;
  const b = p.vectorCompetenceToHost ?? def.b;
  const c = p.vectorCompetenceToVector ?? def.c;
  const eip = p.extrinsicIncubationDays ?? def.eip;
  const lifespan = p.vectorLifespanDays ?? def.lifespan;
  const muV = 1 / lifespan;
  const sigmaV = 1 / eip;
  const survival = sigmaV / (sigmaV + muV);
  // R0² = m·a²·b·c·survival / (μv·γh)  ⇒  m = R0²·μv·γh / (a²·b·c·survival)
  const denom = a * a * b * c * survival;
  const m = denom > 0 ? (p.R0 * p.R0 * muV * p.gammaH) / denom : NaN;
  return {
    vectorToHostRatio: m, a, b, c,
    extrinsicIncubationDays: eip, vectorLifespanDays: lifespan,
    muV, sigmaV, survivalThroughEip: survival,
  };
}

export function simulateVectorBorne(p: VectorBorneParams, opts: SimulateVectorBorneOptions = {}): EpiResult {
  const t0 = performance.now();
  const dt = p.dtDays ?? 0.25;
  const scaler = opts.betaScaler;
  if (p.R0 < 0) throw new Error('VectorBorne: R0 must be ≥ 0');
  if (p.gammaH <= 0 || p.sigmaH <= 0) throw new Error('VectorBorne: gammaH and sigmaH must be positive');
  if (p.ifr < 0 || p.ifr > 1) throw new Error(`VectorBorne: ifr (${p.ifr}) must be in [0,1]`);

  const cal = calibrateVectorBorne(p);
  if (!Number.isFinite(cal.vectorToHostRatio)) throw new Error('VectorBorne: degenerate vector parameters (m not finite)');
  const { a, b, c, muV, sigmaV } = cal;
  const Nh = p.N;
  const E0 = p.E0 ?? 0, R0Pop = p.R0Pop ?? 0, I0 = p.I0;
  const Sh0 = Nh - I0 - E0 - R0Pop;
  if (Sh0 < 0) throw new Error('VectorBorne: N < I0+E0+R0Pop');
  const Nv = cal.vectorToHostRatio * Nh;
  const Iv0 = p.Iv0 ?? 0;
  const Sv0 = Nv - Iv0;
  const sigmaH = p.sigmaH, gammaH = p.gammaH, ifr = p.ifr;

  // state: [Sh, Eh, Ih, Rh, Dh, Sv, Ev, Iv]
  const f = (t: number, y: number[]): number[] => {
    const Sh = y[0]!, Eh = y[1]!, Ih = y[2]!, Sv = y[5]!, Ev = y[6]!, Iv = y[7]!;
    const scale = scaler ? scaler(t) : 1;
    const lambdaH = (scale * a * b * Iv) / Nh; // force of infection on hosts (NPI-modulated)
    const lambdaV = (a * c * Ih) / Nh; // force of infection on vectors
    const newExposedH = lambdaH * Sh;
    const hBecomeInf = sigmaH * Eh;
    const hOut = gammaH * Ih;
    const newExposedV = lambdaV * Sv;
    const vBecomeInf = sigmaV * Ev;
    return [
      -newExposedH,
      newExposedH - hBecomeInf,
      hBecomeInf - hOut,
      hOut * (1 - ifr),
      hOut * ifr,
      muV * Nv - newExposedV - muV * Sv,
      newExposedV - (sigmaV + muV) * Ev,
      vBecomeInf - muV * Iv,
    ];
  };

  const integrated = integrateRk4(f, [Sh0, E0, I0, R0Pop, 0, Sv0, 0, Iv0], p.durationDays, dt);
  const compartments: Record<string, number[]> = { S: [], E: [], I: [], R: [], D: [], Sv: [], Ev: [], Iv: [] };
  for (const row of integrated.y) {
    compartments.S!.push(row[0]!);
    compartments.E!.push(row[1]!);
    compartments.I!.push(row[2]!);
    compartments.R!.push(row[3]!);
    compartments.D!.push(row[4]!);
    compartments.Sv!.push(row[5]!);
    compartments.Ev!.push(row[6]!);
    compartments.Iv!.push(row[7]!);
  }

  return {
    t: integrated.t,
    compartments,
    receipt: {
      engineVersion: ENGINE_VERSION,
      seed: `vector-borne(m=${cal.vectorToHostRatio.toFixed(2)})`,
      elapsedMs: performance.now() - t0,
      steps: integrated.steps,
    },
  };
}
